import { useEffect } from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from '../store/store';

import { Submission } from '../store/submission/types';
import {
  selectCurrentRole,
  selectKeywordState,
  selectSubmissionState,
  selectBidState,
  selectKeywordsLevels,
  selectTrackRoles,
  selectUserKeywordsInCurrentTrack,
  selectTrackUsingKeywords,
  selectCanViewAuthors,
} from '../store/selectors';
import Loading from '../components/Loading/Loading';
import { useParams } from 'react-router-dom';
import { AppState } from '../store/state';
import { useTranslation } from 'react-i18next';
import { BidDTO, BidOption, BidRow, ConflictDTO } from '../store/bid/types';
import bidSlice from '../store/bid/slice';
import styled from 'styled-components';
import KeywordChip from '../components/KeywordChip';
import FileUploaderS3 from '../components/FileUploaderS3/FileUploaderS3';
import { Button, CancelButton } from '../components/ui';
import { goBackFromSubmissionDetail } from '../components/SubmissionDetail/SubmissionDetail';
import { Role } from '../store/conference/types';
import React from 'react';
import EditBidOptionDialog from '../components/dialogs/EditBidOptionDialog/EditBidOptionDialog';
import { getFullName } from '../helpers/users';
import { normalizeAffinity } from '../helpers/submission';

interface Props {
  loading: boolean;
  bidsById: { [key: number]: BidRow };
  keywordState: KeywordState;
  keywordsLevels: KeywordsLevels;
  submissionsById: { [key: string]: Submission };
  roleId?: number;
  roleById: { [key: string]: Role };
  getOwnBidsAction: () => void;
  postConflictAction: (data: ConflictDTO) => void;
  userKeywords: number[];
  trackUsingKeywords: boolean | null;
  canViewAuthors: boolean;
  createBidAction: (data: BidDTO) => void;
  bidOptionsById: { [key: number]: BidOption };
}

const SubmissionDetailPageReviewer = ({
  loading,
  bidsById,
  keywordState,
  keywordsLevels,
  submissionsById,
  roleId,
  roleById,
  getOwnBidsAction,
  postConflictAction,
  userKeywords,
  trackUsingKeywords,
  canViewAuthors,
  createBidAction,
  bidOptionsById,
}: Props) => {
  const { t } = useTranslation();
  const params: any = useParams();
  const { id: bidable_id } = params;
  const [mobileBidSubmissionDialog, setMobileBidSubmissionDialog] = React.useState<JSX.Element | undefined>(undefined);

  // Given id must match with bidable_id field
  const findSubmission = Object.values(submissionsById).find((submission) => submission.id == bidable_id);

  useEffect(() => {
    if (Object.keys(bidsById).length === 0) {
      getOwnBidsAction();
    }
  }, []);

  const submission = Object.values(bidsById).find((submission) => submission.bidable_id == bidable_id);

  if (loading || !roleId) {
    return <Loading />;
  } else if (!submission) {
    return <div>{t('Submission #{{id}} not found', { id: bidable_id })}</div>;
  }

  const currentRole = roleById[roleId];
  const currentRoleType = currentRole.type;
  const { keywordById, keywordTree } = keywordState;
  const hasKeywords = trackUsingKeywords && Object.keys(keywordTree).length > 0 && userKeywords.length > 0;
  const paperUpload = findSubmission?.file_upload;

  return (
    <StyledSubmissionDetailWrapper className="mobileCtrlSubmissionDetailBiddingWrapper" style={{ flexGrow: 1 }}>
      <div className="mobileCtrlSubmissionDetailBiddingContent">
        <div className="font-bold mb-8 text-xl">{submission.title}</div>
        {hasKeywords && (
          <div className="mb-8 text-sm">
            <p className="font-bold mb-2">{t('Affinity')}</p>
            <p>{normalizeAffinity(submission.affinity)}</p>
          </div>
        )}
        <div className="mb-8 text-sm">
          <p className="font-bold mb-2">{t('Abstract')}</p>
          <p>{submission.abstract}</p>
        </div>
        <div className="mb-8 text-sm">
          {submission.authors.length > 0 && canViewAuthors && (
            <p className="text-sm line-clamp">
              <span className="font-bold">{t('Authors') + ': '}</span>
              {submission.authors.map((author) => `${getFullName(author)}`).join(', ')}
            </p>
          )}
        </div>
        {hasKeywords && (
          <div className="mb-8 text-sm">
            <p className="font-bold mb-2">{t('Keywords')}</p>
            {submission.keyword_ids.length > 0 ? (
              <StyledKeywordsContent className="w-full mt-2">
                {submission.keyword_ids.map((keywordId, index) => {
                  const keyword = keywordById[keywordId];
                  const parentKeywordName = keyword.parent_id ? keywordById[keyword.parent_id].name : '';
                  return (
                    <KeywordChip
                      key={index}
                      areaName={parentKeywordName}
                      topicName={keyword.name}
                      keywordsLevels={keywordsLevels}
                    />
                  );
                })}
              </StyledKeywordsContent>
            ) : (
              <div className="mt-2">{t('No keywords selected')}</div>
            )}
          </div>
        )}

        {paperUpload && (
          <div id="uploaded-paper" className="mb-8 text-sm">
            <p className="font-bold mb-2">{t('Paper upload')}</p>
            <StyledFileUploaderS3 className="mt-2">
              <FileUploaderS3 initUpload={paperUpload} disabled={true} />
            </StyledFileUploaderS3>
          </div>
        )}
      </div>

      {/* FOOTER BUTTONS */}
      <StyledButtonsWrapper className="mobileCtrlSubmissionDetailFooterButton">
        <CancelButton onClick={() => goBackFromSubmissionDetail(currentRoleType)}>{t('Back')}</CancelButton>
        <Button
          className="w-48 mx-2 mobileCtrlBidButton"
          onClick={() => {
            setMobileBidSubmissionDialog(
              <EditBidOptionDialog
                submission={submission}
                handleClose={() => setMobileBidSubmissionDialog(undefined)}
                bidOptionsById={bidOptionsById}
                createBidAction={createBidAction}
                postConflict={postConflictAction}
              />,
            );
          }}
        >
          {t('Bid submission')}
        </Button>
      </StyledButtonsWrapper>

      {mobileBidSubmissionDialog}
    </StyledSubmissionDetailWrapper>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectBidState(state).loading || selectBidState(state).bidOptions.loading,
  bidsById: selectBidState(state).byId,
  keywordById: selectKeywordState(state).keywordById,
  keywordsLevels: selectKeywordsLevels(state),
  keywordState: selectKeywordState(state),
  submissionsById: selectSubmissionState(state).submissionsById,
  roleId: selectCurrentRole(state)?.id,
  roleById: selectTrackRoles(state),
  userKeywords: selectUserKeywordsInCurrentTrack(state),
  trackUsingKeywords: selectTrackUsingKeywords(state),
  canViewAuthors: selectCanViewAuthors(state),
  bidOptionsById: selectBidState(state).bidOptions.byId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getOwnBidsAction: () => dispatch(bidSlice.actions['GET:BIDS:OWN']()),
  postConflictAction: (data: ConflictDTO) => dispatch(bidSlice.actions['POST:CONFLICT'](data)),
  createBidAction: (data: BidDTO) => dispatch(bidSlice.actions['POST:BID'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionDetailPageReviewer);

const StyledKeywordsContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-wrap: wrap;
  div {
    margin-right: 0.5rem;
  }

  .keywordds-chip {
    padding-top: 6px;
    padding-bottom: 6px;
    height: auto;
    padding-left: 18px;
    padding-right: 18px;

    span {
      margin-right: 10px;

      p:first-child {
        margin-bottom: 6px;
      }
    }
  }
`;

const StyledFileUploaderS3 = styled.div`
  width: 100%;
`;

const StyledSubmissionDetailWrapper = styled.div`
  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
  .read-more-button {
    color: ${(props) => props.theme.palette.primary.main};
  }
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: sticky;
  width: 100%;
  bottom: 0;
  padding: 1rem 0;
  background-color: #fff;
  border-top: 1px dashed ${(props) => props.theme.palette.secondary.dark};
  z-index: 2;
`;
